import { PageHeader, Tabs, TabsContent, TabsList, TabsTrigger, TOUCH } from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { CalendarSection } from "@/components/calendar/CalendarSection";
import { PersonProfileCard } from "@/components/profile/PersonProfileCard";
import { ProfileAvatarCard } from "@/components/profile/ProfileAvatarCard";
import { ProfileInfoCard } from "@/components/profile/ProfileInfoCard";
import { useCalendarStatus } from "@/hooks/use-calendar";
import { PROFILE_PAGE_TAB_LABELS, PROFILE_PAGE_TABS } from "@/lib/enums";
import { ApiError } from "@/lib/time/api-client";
import type { ProfileSearch } from "@/schemas/calendar";

/**
 * /dashboard/profile: two tabs, Profile (default) and Calendar (`?tab=calendar`), so the calendar
 * rules don't fill the page. Without a calendar to show (Microsoft sign-in not set up, or an admin
 * impersonating) there are no tabs, only the profile. `?rule=new` opens Add rule (on the Calendar tab); `?calendar=error`
 * is a failed Connect calendar.
 */
export function ProfilePage() {
  const search = useSearch({ from: "/dashboard/profile" });
  const navigate = useNavigate({ from: "/dashboard/profile" });
  const setSearch = (next: Partial<ProfileSearch>, replace = false) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), replace });
  const status = useCalendarStatus();
  const noCalendar =
    (status.isSuccess && !status.data.available) ||
    (status.isError && status.error instanceof ApiError && status.error.status === 403);
  const tab = noCalendar
    ? "profile"
    : search.rule === "new"
      ? "calendar"
      : (search.tab ?? "profile");

  // Say a failed Connect calendar once, then drop the param.
  const calendarError = search.calendar === "error";
  useEffect(() => {
    if (!calendarError) return;
    toast.error("Calendar not connected", { id: "calendar-error" });
    void navigate({ search: (prev) => ({ ...prev, calendar: undefined }), replace: true });
  }, [calendarError, navigate]);

  return (
    <Tabs
      value={tab}
      onValueChange={(v) =>
        setSearch({ tab: v === "calendar" ? "calendar" : undefined, rule: undefined })
      }
      className="gap-6"
    >
      <PageHeader title="Profile" />
      {!noCalendar && (
        <TabsList aria-label="Profile view">
          {PROFILE_PAGE_TABS.map((t) => (
            <TabsTrigger key={t} value={t} className={TOUCH}>
              {PROFILE_PAGE_TAB_LABELS[t]}
            </TabsTrigger>
          ))}
        </TabsList>
      )}
      <TabsContent value="profile" className="space-y-6">
        <PersonProfileCard />
        <ProfileInfoCard />
        {/* --- S3_FEATURE_START --- */}
        <ProfileAvatarCard />
        {/* --- S3_FEATURE_END --- */}
      </TabsContent>
      <TabsContent value="calendar">
        <CalendarSection
          adding={search.rule === "new"}
          onAddingChange={(open) =>
            setSearch({ tab: "calendar", rule: open ? "new" : undefined }, !open)
          }
        />
      </TabsContent>
    </Tabs>
  );
}
